'use client'
import { type FC, type ReactNode, createContext, useState, useCallback, useRef, useMemo, } from 'react'
import { AnimatePresence } from 'motion/react'
import { Toast, ToastWrapper, type iToastProps } from '@/components'

interface iToastState extends iToastProps {
  id: number
}

export interface ToastContextProps {
  addToast: (toastData: iToastProps) => void
}

type Props = {
  children: ReactNode
	dataPosition?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
}

export const ToastContext = createContext<ToastContextProps | undefined>(undefined)

export const ToastProvider: FC<Props> = ({ children, dataPosition = 'bottom-left' }) => {
  const [toasts, setToasts] = useState<iToastState[]>([])
  const toastIdRef = useRef(0)

  const addToast = useCallback((toastData: iToastProps) => {
    setToasts((prevToasts) => [
      ...prevToasts,
      { ...toastData, id: toastIdRef.current++ },
    ])
  }, [])

  const hasBlockingToast = useMemo(() => toasts.some((t) => t.blockInteraction), [toasts])
  const contextValue = useMemo(() => ({ addToast }), [addToast])

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastWrapper
        aria-live='assertive'
        aria-atomic='true'
        data-position={dataPosition}
        {...toasts.length > 0 && { 'data-has-toasts': true }}
        {...hasBlockingToast && { 'data-block-interaction': true }}
      >
        <AnimatePresence mode='wait'>
          {toasts.map((toast) => (
            <Toast
              key={toast.id}
              {...toast}
              onClose={() => setToasts((prev) => prev.filter((t) => t.id !== toast.id))}
            />
          ))}
        </AnimatePresence>
      </ToastWrapper>
    </ToastContext.Provider>
  )
}
